import React, { useEffect, useState } from "react";
import AddressTable from "../../components/address/AddressTable";
import { CustomerFilters } from "../../../../../models/Customer/CustomerFilters";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { SortType } from "../../../../../models/SortType";
import AddIcon from "@mui/icons-material/Add";
import { Box, CircularProgress, Container, Typography } from "@mui/material";
import PageTitle from "../../../../../views/components/common/pages/PageTitle";
import SearchIcon from "@mui/icons-material/Search";
import { useStores } from "../../../use-stores";
import DeleteConfirmation from "../../../../../views/components/common/pop-up/DeleteConfirmation";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import { AddressFilters } from "../../../../../models/Address/AddressFilters";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";

const AddressListing = () => {
  const { addressStore, userStore } = useStores();

  const { showError, showSuccess } = useNotification();

  const [deleteId, setDeleteId] = useState<number | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialParams: AddressFilters = {
    page_no: searchParams.get("page")
      ? parseInt(searchParams.get("page") as string, 10)
      : 1,
    search_text: searchParams.get("search_text") || undefined,
    active: searchParams.get("active")
      ? searchParams.get("active") === "false"
        ? false
        : true
      : true,
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit") as string, 10)
      : 30,
    sort_by: searchParams.get("sort_by") || "createdDate",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
  };

  const [params, setParams] = useState(initialParams);

  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || ""
  );

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (
      userStore.hasPermission([
        EmployeeRoles.VIEW_ADDRESS,
        EmployeeRoles.VIEW_ORDER,
      ])
    ) {
      setIsLoading(true);
      addressStore
        .getAddress(params)
        .catch((err) =>
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later."
          )
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    const updateQueryParams = () => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (
          value !== undefined &&
          (!Array.isArray(value) || value.length > 0)
        ) {
          const paramValue = Array.isArray(value)
            ? value.join(",")
            : value.toString();
          searchParams.set(key, paramValue);
        }
      });

      const queryString = searchParams.toString();
      if (queryString) {
        navigate({ search: queryString }, { replace: true });
      }
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, navigate]);

  const handleSearchBar = async (value: string) => {
    if (value) {
      setSearchTerm(value);
    } else {
      setSearchTerm("");
      setParams((prev) => ({
        ...prev,
        search_text: "",
      }));
    }
  };

  const handleSearchSubmit = () => {
    setParams((prev) => ({
      ...prev,
      search_text: searchTerm,
      page_no: 1,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 0) {
        setParams((prev) => ({
          ...prev,
          search_text: searchTerm,
          page_no: 1,
        }));
      } else {
        setSearchTerm("");
        setParams((prev) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setParams((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleDeleteConfirm = async (id: number) => {
    setIsLoading(true);
    if (id) {
      const res = await addressStore.deleteAddress(id);
      setDeleteId(null);
      if (res?.success) {
        showSuccess(res?.result);
        addressStore
          .getAddress(params)
          .catch((err) =>
            showError(
              "Error",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later."
            )
          )
          .finally(() => setIsLoading(false));
      } else {
        setIsLoading(false);
        showError("Error Occurred", "Error deleting address");
      }
    }
  };

  return (
    <Container maxWidth="xl" sx={{ paddingTop: 2 }}>
      {!userStore.hasPermission([
        EmployeeRoles.VIEW_ADDRESS,
        EmployeeRoles.VIEW_ORDER,
      ]) ? (
        <PermissionBlockLabel />
      ) : (
        <Box
          sx={{
            marginTop: "0.6rem",
            pt: 5,
            pl: 6,
            pr: 6,
            pb: 1,
            boxShadow:
              "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
            borderRadius: "10px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              paddingBottom: 1,
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <PageTitle title="Address" />

            <Link to={"/address/add-edit-address"}>
              <AppButton
                onClick={() => {}}
                label="Add Address"
                startIcon={<AddIcon />}
              />
            </Link>
          </Box>

          <Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                paddingBottom: 3,
                justifyContent: "space-between",
                alignContent: "center",
              }}
            >
              <CustomTextField
                placeholder="Search by name, email"
                value={searchTerm}
                onSearch={(e) => handleSearchBar(e)}
                onKeyDown={(e) => handleKeyDown(e)}
                endAdornment={
                  <SearchIcon
                    onClick={(e) => handleSearchSubmit()}
                    sx={{ color: "#7C7A7F", fontSize: "20px" }}
                  />
                }
                customStyles={{
                  width: "90%",
                }}
              />
              <AppButton
                onClick={() => {}}
                label="Filter"
                variant="grayscale"
              />
            </Box>
          </Box>
          {!isLoading && addressStore.addresses.length === 0 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="10rem"
              textAlign="center"
            >
              <Typography variant="h6" fontWeight="500" color="text.secondary">
                No results found for "{searchTerm}"
              </Typography>
            </Box>
          )}
          {isLoading && (
            <Box
              sx={{
                minHeight: "60vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {!isLoading && (
            <Box>
              {addressStore.addresses.length > 0 && (
                <AddressTable
                  maxHeight={window.innerHeight - 350}
                  addresses={addressStore.addresses}
                  limit={params.limit || addressStore.limit}
                  page_no={params.page_no!}
                  total_count={addressStore.total_count}
                  total_pages={addressStore.total_pages}
                  sortBy={params.sort_by}
                  sortType={params.sort_type}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  onDelete={(id) => setDeleteId(id)}
                  onSortChange={(column, sortType) => {
                    setParams((prev) => ({
                      ...prev,
                      sort_by: column,
                      sort_type: sortType,
                    }));
                  }}
                />
              )}
            </Box>
          )}
          {deleteId !== null && (
            <DeleteConfirmation
              id={deleteId}
              open={true}
              onDeleteConfirm={() => handleDeleteConfirm(deleteId)}
              onCancel={() => setDeleteId(null)}
              title="Confirm Deletion"
              content="Are you sure you want to delete this user?"
            />
          )}
        </Box>
      )}
    </Container>
  );
};

export default AddressListing;
